<script lang="ts">
  import { createEventDispatcher, onDestroy, onMount, tick } from "svelte";
  import type { LocalOpportunityPayload } from "../lib/types";
  import { prune } from "../lib/prune";
  import { eventBus } from "../lib/eventBus";
  import { isAwsCatalog, normalizeDate } from "../lib/utils";
  import { triggerOpportunityDataRefresh } from "../lib/syncDashboardRefresh";
  import {
    buildOpportunityValidationSummary,
    mapOpportunityApiErrors,
  } from "../lib/opportunityValidationMessages";
  import {
    applyMandatoryFieldFillToForm,
    buildMandatoryFieldFillSummaryMessages,
  } from "../lib/mandatoryFieldFillUi";
  import { opportunityOptions } from "../lib/opportunityOptionsStore";
  import { createBusinessRuleEvaluator } from "../lib/businessRuleEngine";
  import { isFieldEditableForAwsReviewStatus } from "../lib/awsOpportunityUpdateRules";
  import { themeCssVars } from "../lib/theme";
  import MultiCheckboxDropdown from "./multiCheckboxDropdown.svelte";
  import FieldLabel from "./fieldLabel.svelte";
  import AwsLockIcon from "./awsLockIcon.svelte";

  const dispatch = createEventDispatcher();

  const DEFAULT_URL = process.env.SVELTE_APP_REMOTE_URL;
  const baseUrl = `${DEFAULT_URL}/api/sync/opportunity`;
  const SETTINGS_URL = `${process.env.SVELTE_APP_FE_REMOTE_URL}/api/common-config/settings`;

  let showForm = false;
  let awsOppId = "";
  let isSaving = false;
  let isFillingMandatoryFields = false;
  let apiError: string[] = [];
  let savedAwsReviewStatus: string = "";
  let showSuccessModal = false;
  let successMessage = "The opportunity has been successfully saved to AWS and your local system.";
  let successHasWarning = false;
  let successStageWarning: string = "";
  let successPreservedAwsState = false;
  let successCloseConfirmedAfterError = false;
  let successStageConfirmedAfterError = false;
  let successTerminalStage = "";
  let solutions: { Id: string; Name: string }[] = [];
  let currencyCodes: string[] = [];
  let products: { Identifier: string; Name: string; Family: string; Description: string }[] = [];
  let useCaseOptions: string[] = [];
  let usStates: string[] = [];
  let countries: { label: string; value: string }[] = [];
  let postalCodeRules: Record<string, RegExp> = {};
  let showNextStepsHistory = false;
  let lastCommittedNextSteps = "";
  let nextStepsHistoryMaxItems = 50;
  let nextStepsHistoryItems: { Time: string; Value: string }[] = [];
  let isUpdateMode = false;
  // Keeps non-editable fields unchanged on update.
  let loadedFormSnapshot: LocalOpportunityPayload | null = null;
  let awsCatalog = "sandbox";
  let reviewStatusSelectOptions: { value: string; label: string; disabled: boolean }[] = [];
  let validationSummaryMessages: string[] = [];
  let validationSummaryElement: HTMLDivElement | null = null;
  let fillSummaryMessages: string[] = [];
  let showMandatoryFillPanel = false;
  let currentOpportunityId: number | null = null;

  const CREATE_REVIEW_STATUS = "Pending Submission";
  const DEFAULT_ORIGIN = "Partner Referral";
  const DEFAULT_EXPECTED_CUSTOMER_SPEND_CURRENCY_CODE = "USD";
  const DEFAULT_EXPECTED_CUSTOMER_SPEND_FREQUENCY = "Monthly";
  const DEFAULT_EXPECTED_CUSTOMER_SPEND_TARGET_COMPANY = "AWS";
  const EXPECTED_CUSTOMER_SPEND_FREQUENCY_OPTIONS = ["Monthly"];
  const EXPECTED_CUSTOMER_SPEND_TARGET_COMPANY_OPTIONS = ["AWS"];
  const SANDBOX_UPDATE_ALLOWED_REVIEW_STATUSES = [
    "Submitted",
    "Approved",
    "Rejected",
    "Action Required",
  ];
  const AWS_UPDATE_ALLOWED_REVIEW_STATUSES = [
    "Pending Submission",
    "Submitted",
  ];

  // Business rule evaluator
  const ruleEvaluator = createBusinessRuleEvaluator({
    baseUrl: process.env.SVELTE_APP_ALADV_BASE_URL || '',
    ruleName: 'opportunity-validation'
  });

  const lifecycleRuleEvaluator = createBusinessRuleEvaluator({
    baseUrl: process.env.SVELTE_APP_ALADV_BASE_URL || '',
    ruleName: 'opportunity-lifecycle-validation'
  });

  const reviewStatusRuleEvaluator = createBusinessRuleEvaluator({
    baseUrl: process.env.SVELTE_APP_ALADV_BASE_URL || '',
    ruleName: 'opportunity-review-status-validation'
  });

  $: updateAllowedReviewStatuses = isAwsCatalog(awsCatalog)
    ? AWS_UPDATE_ALLOWED_REVIEW_STATUSES
    : SANDBOX_UPDATE_ALLOWED_REVIEW_STATUSES;
  $: updateAllowedReviewStatusSet = new Set(updateAllowedReviewStatuses);

  // Extract default form into a constant
  const defaultForm: LocalOpportunityPayload = {
    opportunityType: undefined,
    parentOppId: "",
    partnerCrmUniqueId: "",
    origin: DEFAULT_ORIGIN,
    syncToAws: "1",
    partnerPrimaryNeedFromAws: [],
    customerCompanyName: "",
    customerDuns: "",
    industryVertical: undefined,
    industryOther: "",
    website: "",
    awsAccountId: "",
    streetAddress: "",
    city: "",
    state: "",
    country: "",
    postalCode: "",
    customerContacts: [{ firstName: "", lastName: "", email: "", businessTitle: "", phone: "" }],
    targetCloseDate: "",
    awsStage: "Prospect",
    reviewStatus: CREATE_REVIEW_STATUS,
    closedLostReason: "",
    nextSteps: "",
    nextStepsHistory: [],
    opportunityTeam: [],
    awsProducts: [],
    solutionOffered: [],
    estimatedAwsMonthlyReccuringRevenue: undefined,
    expectedCustomerSpendCurrencyCode: DEFAULT_EXPECTED_CUSTOMER_SPEND_CURRENCY_CODE,
    expectedCustomerSpendFrequency: DEFAULT_EXPECTED_CUSTOMER_SPEND_FREQUENCY,
    expectedCustomerSpendTargetCompany: DEFAULT_EXPECTED_CUSTOMER_SPEND_TARGET_COMPANY,
    partnerProjectTitle: "",
    customerBusinessProblem: "",
    otherSolOffered: "",
    useCase: "",
    deliveryModel: [],
    salesActivities: [],
    competitiveTracking: undefined,
    otherCompetitors: "",
    apnPrograms: [],
    additionalComments: "",
    mandatoryFieldContext: "",
    mandatoryFieldFillLog: "",
    awsFundingUsed: "",
    marketingCampaign: "",
    marketingActivityChannel: [],
    marketingSource: undefined,
    marketingActivityUseCase: [],
    doesOppBelongToNatSec: undefined,
  };

  const createDefaultForm = (): LocalOpportunityPayload => ({
    ...defaultForm,
    partnerPrimaryNeedFromAws: [],
    customerContacts: [{ firstName: "", lastName: "", email: "", businessTitle: "", phone: "" }],
    nextStepsHistory: [],
    opportunityTeam: [],
    awsProducts: [],
    solutionOffered: [],
    deliveryModel: [],
    salesActivities: [],
    apnPrograms: [],
    marketingActivityChannel: [],
    marketingActivityUseCase: [],
  });

  // Default form state
  let form: LocalOpportunityPayload = createDefaultForm();

  // Reset function
  const resetForm = (): void => {
    ruleEvaluator.clearCache();
    lifecycleRuleEvaluator.clearCache();
    reviewStatusRuleEvaluator.clearCache();
    form = createDefaultForm();
    awsOppId = "";
    currentOpportunityId = null;
    savedAwsReviewStatus = "";
    loadedFormSnapshot = null;
    errors = {};
    apiError = [];
    fillSummaryMessages = [];
    showMandatoryFillPanel = false;
    showNextStepsHistory = false;
    lastCommittedNextSteps = "";
    successHasWarning = false;
    successStageWarning = "";
    successPreservedAwsState = false;
    successCloseConfirmedAfterError = false;
    successStageConfirmedAfterError = false;
    successTerminalStage = "";
  };

  let errors: Record<string, string> = {};

  $: validationSummaryMessages = buildOpportunityValidationSummary(errors, apiError);

  const scrollToValidationSummary = async (): Promise<void> => {
    await tick();
    validationSummaryElement?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
    });
  };

  const applyApiErrors = (resp: any) => {
    const { apiError: nextApiError, fieldErrors } = mapOpportunityApiErrors(resp);
    if (nextApiError.length === 0) return;
    apiError = nextApiError;
    errors = { ...errors, ...fieldErrors };
  };

  const normalizeNextStepsHistory = (value: any): { Time: string; Value: string }[] => {
    const parseJson = (raw: unknown): unknown => {
      if (typeof raw !== "string") return raw;
      try {
        return JSON.parse(raw);
      } catch {
        return null;
      }
    };

    const source = parseJson(value);
    if (!Array.isArray(source)) return [];

    return source.flatMap((entry) => {
      const item = parseJson(entry);
      if (
        item &&
        typeof item === "object" &&
        typeof (item as any).Time === "string" &&
        typeof (item as any).Value === "string"
      ) {
        return [{ Time: (item as any).Time, Value: (item as any).Value }];
      }
      return [];
    });
  };

  const formatHistoryTime = (value?: string): string => {
    if (!value) return "-";
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return value;
    return parsed.toLocaleString();
  };

  const commitNextStepsHistory = (): { Time: string; Value: string }[] => {
    const currentValue = String(form.nextSteps ?? "");
    const previousValue = String(lastCommittedNextSteps ?? "");
    const existingHistory = normalizeNextStepsHistory(form.nextStepsHistory);

    if (currentValue === previousValue) return existingHistory;

    let nextHistory = existingHistory;
    if (previousValue !== "") {
      nextHistory = [
        { Time: new Date().toISOString(), Value: previousValue },
        ...existingHistory,
      ].slice(0, nextStepsHistoryMaxItems);
      form = {
        ...form,
        nextStepsHistory: nextHistory,
      };
    }

    lastCommittedNextSteps = currentValue;
    return nextHistory;
  };

  const normalizeEstimatedAwsMonthlyReccuringRevenue = (
    value: number | null | undefined
  ): number | undefined => {
    if (typeof value !== "number" || Number.isNaN(value)) return undefined;
    return Math.max(0, Math.trunc(value));
  };

  const handleEstimatedAwsMonthlyReccuringRevenueInput = (event: Event): void => {
    const input = event.currentTarget as HTMLInputElement;
    const normalizedValue = normalizeEstimatedAwsMonthlyReccuringRevenue(input.valueAsNumber);
    form = {
      ...form,
      estimatedAwsMonthlyReccuringRevenue: normalizedValue,
    };
    input.value = normalizedValue?.toString() ?? "";
  };

  const getReviewStatusForSubmit = (): string | undefined => {
    const normalized = typeof form.reviewStatus === "string" ? form.reviewStatus.trim() : "";
    return updateAllowedReviewStatusSet.has(normalized) ? normalized : undefined;
  };

  // Lock by the status saved at AWS, not by the one being selected in the form.
  let reviewStatusForFieldLock = "";
  $: reviewStatusForFieldLock = isUpdateMode ? savedAwsReviewStatus.trim() : "";

  const canEditAwsField = (field: string, reviewStatus: string): boolean =>
    isFieldEditableForAwsReviewStatus(field, reviewStatus);

  const normalizePopupSolutionSelections = (
    values: string[] | undefined,
    otherSolutionValue: string | undefined
  ): string[] => {
    const selections = (values ?? []).map((value) => (value === "Other" ? "__other__" : value));
    const concreteSelections = selections.filter((value) => value !== "__other__");
    if (concreteSelections.length > 0) {
      return concreteSelections;
    }
    if (otherSolutionValue?.trim() && !selections.includes("__other__")) {
      return [...selections, "__other__"];
    }
    return selections;
  };

  const getEffectiveSolutionSelections = (values: string[] | undefined): string[] => {
    const selections = values ?? [];
    const concreteSelections = selections.filter((value) => value !== "__other__" && value !== "Other");
    return concreteSelections.length > 0 ? concreteSelections : selections;
  };

  $: if (Array.isArray(form.solutionOffered)) {
    const effectiveSolutions = getEffectiveSolutionSelections(form.solutionOffered);
    if (effectiveSolutions.length !== form.solutionOffered.length) {
      form.solutionOffered = effectiveSolutions;
    }
    if (
      !effectiveSolutions.includes("__other__") &&
      form.otherSolOffered
    ) {
      form.otherSolOffered = "";
    }
  }

  const buildSyncPayload = (
    crmId: string,
    reviewStatusForSubmit: string | undefined,
    nextStepsHistory: { Time: string; Value: string }[],
    src: LocalOpportunityPayload = form
  ) => {
    const normalizedCrmId = crmId.trim() || undefined;
    const selectedSolutions = getEffectiveSolutionSelections(src.solutionOffered);
    const includesOtherSolution =
      selectedSolutions.includes("__other__") || selectedSolutions.includes("Other");

    return {
      ...prune({
        ...src,
        syncToAws: "1",
        origin: src.origin || DEFAULT_ORIGIN,
        reviewStatus: reviewStatusForSubmit,
        nextStepsHistory,
        targetCloseDate: normalizeDate(src.targetCloseDate),
        partnerCrmUniqueId: normalizedCrmId,
        expectedCustomerSpendCurrencyCode:
          src.expectedCustomerSpendCurrencyCode ||
          DEFAULT_EXPECTED_CUSTOMER_SPEND_CURRENCY_CODE,
        expectedCustomerSpendFrequency:
          src.expectedCustomerSpendFrequency ||
          DEFAULT_EXPECTED_CUSTOMER_SPEND_FREQUENCY,
        expectedCustomerSpendTargetCompany:
          src.expectedCustomerSpendTargetCompany ||
          DEFAULT_EXPECTED_CUSTOMER_SPEND_TARGET_COMPANY,
      }),
      awsProducts: src.awsProducts ?? [],
      solutionOffered: selectedSolutions.map((value) =>
        value === "__other__" ? "Other" : value
      ),
      otherSolOffered: includesOtherSolution ? src.otherSolOffered : undefined,
    };
  };

  const NON_RESTORABLE_FIELDS = new Set(["syncToAws", "reviewStatus", "nextStepsHistory"]);

  const lockedFieldPredicate = (): ((key: string) => boolean) | null => {
    if (!isUpdateMode) return null;
    const status = savedAwsReviewStatus.trim();
    return (k) => !isFieldEditableForAwsReviewStatus(k, status);
  };

  // AWS rejects changes to locked fields but requires them in the payload,
  // so locked fields are sent with their originally loaded values.
  const applyEditableFieldRestrictions = (
    current: LocalOpportunityPayload,
    original: LocalOpportunityPayload | null
  ): LocalOpportunityPayload => {
    const isLocked = lockedFieldPredicate();
    if (!isLocked || !original) return current;
    const restored: Record<string, any> = { ...current };
    for (const key of Object.keys(current)) {
      if (isLocked(key) && !NON_RESTORABLE_FIELDS.has(key) && key in original) {
        restored[key] = (original as Record<string, any>)[key];
      }
    }
    return restored as LocalOpportunityPayload;
  };

  const generatePartnerCrmId = (): string =>
    `hs-${Math.floor(Math.random() * 1e12).toString().padStart(12, "0")}-${Date.now()}`;

  const ensurePartnerCrmId = (): void => {
    const existing = loadedFormSnapshot?.partnerCrmUniqueId?.trim();
    if (existing) {
      if (form.partnerCrmUniqueId !== existing) {
        form = { ...form, partnerCrmUniqueId: existing };
      }
      return;
    }
    if (!form.partnerCrmUniqueId || !form.partnerCrmUniqueId.trim()) {
      form = { ...form, partnerCrmUniqueId: generatePartnerCrmId() };
    }
  };

  const handleSubmit = async () => {
    // Clear previous errors
    errors = {};
    apiError = [];
    fillSummaryMessages = [];

    ensurePartnerCrmId();

    const nextStepsHistory = commitNextStepsHistory();
    const reviewStatusForSubmit = getReviewStatusForSubmit();

    const ruleErrors = await evaluateOpportunityFormRules(nextStepsHistory);
    if (Object.keys(ruleErrors).length > 0) {
      errors = { ...errors, ...ruleErrors };
      if (ruleErrors._general) {
        apiError = [ruleErrors._general];
      }
      await scrollToValidationSummary();
      return;
    }
    
    // Continue with submission
    isSaving = true;
    apiError = [];
    const crmId = form.partnerCrmUniqueId ?? "";
    try {
      const url = awsOppId ? `${baseUrl}?opportunityId=${awsOppId}` : baseUrl;
      const payloadSrc = applyEditableFieldRestrictions(form, loadedFormSnapshot);
      const payload = buildSyncPayload(
        crmId,
        reviewStatusForSubmit,
        nextStepsHistory,
        payloadSrc
      );

      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json();

      if (!res.ok || data.success === false) {
        applyApiErrors(data);
        if (!apiError || (Array.isArray(apiError) && apiError.length === 0)) {
          apiError = [data?.response?.Message || data?.response?.error || data?.Message || `Request failed: ${res.status}`];
        }
        await scrollToValidationSummary();
        return;
      }

      dispatch("saved", data);
      successHasWarning = !!data.reviewStatusWarning;
      successStageWarning = data.stageWarning || "";
      successPreservedAwsState = !!data.preserveAwsState;
      successCloseConfirmedAfterError = !!data.closeConfirmedAfterError;
      successStageConfirmedAfterError = !!data.stageConfirmedAfterError;
      successTerminalStage = data?.response?.LifeCycle?.Stage || form.awsStage || "the requested stage";
      successMessage = successCloseConfirmedAfterError || successStageConfirmedAfterError
        ? `The opportunity was updated to ${successTerminalStage} in AWS. Your local record was refreshed from AWS.`
        : successPreservedAwsState
          ? `This opportunity is already ${successTerminalStage} in AWS. Your local record was refreshed from AWS, and no new changes were saved.`
          : "The opportunity has been successfully saved to AWS and your local system.";
      showSuccessModal = true;
    } catch (err: any) {
      apiError = [err instanceof Error ? err.message : "An unexpected error occurred while saving."];
      await scrollToValidationSummary();
    } finally {
      isSaving = false;
    }
  };

  const fetchAwsOpportunity = async (): Promise<void> => {
    if (!awsOppId) {
      apiError = ["Please enter an AWS Opportunity Id first."];
      return;
    }
    isSaving = true;
    apiError = [];
    try {
      const res = await fetch(`${baseUrl}/${awsOppId}`);
      const data = await res.json();
      if (!res.ok || data.success === false) {
        if (data?.response?.ErrorList?.length) {
          apiError = data.response.ErrorList.map(
            (e: any) =>
              `${e.Code || "Error"}${e.FieldName ? ` (${e.FieldName})` : ""}: ${e.Message}`
          );
        } else {
          apiError = [
            data?.response?.error ||
              data?.response?.Message ||
              `Failed to fetch AWS Opportunity: ${res.status}`,
          ];
        }
        return;
      }
      const awsLifeCycle = data?.response?.LifeCycle ?? {};
      const mappedHistory = normalizeNextStepsHistory(data?.payload?.nextStepsHistory);
      const awsHistory = normalizeNextStepsHistory(awsLifeCycle.NextStepsHistory);
      savedAwsReviewStatus = (data?.payload?.reviewStatus ?? awsLifeCycle.ReviewStatus ?? "").trim();

      form = {
        ...form,
        ...data.payload,
        doesOppBelongToNatSec: preferDefinedValue(
          data?.payload?.doesOppBelongToNatSec,
          form.doesOppBelongToNatSec
        ),
        apnPrograms: preferNonEmptyArray(data?.payload?.apnPrograms, form.apnPrograms),
        solutionOffered: normalizePopupSolutionSelections(
          data?.payload?.solutionOffered,
          data?.payload?.otherSolOffered
        ),
        estimatedAwsMonthlyReccuringRevenue: normalizeEstimatedAwsMonthlyReccuringRevenue(
          data?.payload?.estimatedAwsMonthlyReccuringRevenue ??
            form.estimatedAwsMonthlyReccuringRevenue
        ),
        reviewStatus: data?.payload?.reviewStatus ?? awsLifeCycle.ReviewStatus ?? "",
        nextSteps: data?.payload?.nextSteps ?? awsLifeCycle.NextSteps ?? "",
        nextStepsHistory: mappedHistory.length > 0 ? mappedHistory : awsHistory,
      };
      lastCommittedNextSteps = String(form.nextSteps ?? "");
      loadedFormSnapshot = JSON.parse(JSON.stringify(form));
    } catch (err: any) {
      apiError = [err instanceof Error ? err.message : "Failed to fetch AWS opportunity."];
    } finally {
      isSaving = false;
    }
  };

  const setPopupError = (message: string): void => {
    apiError = [message];
  };

  const preferDefinedValue = <T>(nextValue: T | null | undefined, currentValue: T): T =>
    nextValue ?? currentValue;

  const preferNonEmptyArray = <T>(
    nextValue: T[] | null | undefined,
    currentValue: T[] | undefined
  ): T[] | undefined =>
    Array.isArray(nextValue) ? (nextValue.length > 0 ? nextValue : currentValue) : currentValue;

  const applyLocalPayloadToForm = (payload: LocalOpportunityPayload | undefined): void => {
    if (!payload) return;

    form = {
      ...form,
      ...payload,
      targetCloseDate: normalizeDate(payload.targetCloseDate) ?? "",
      solutionOffered: normalizePopupSolutionSelections(
        payload.solutionOffered,
        payload.otherSolOffered
      ),
      estimatedAwsMonthlyReccuringRevenue: normalizeEstimatedAwsMonthlyReccuringRevenue(
        payload.estimatedAwsMonthlyReccuringRevenue
      ),
      nextStepsHistory: normalizeNextStepsHistory(payload.nextStepsHistory),
      syncToAws: payload.syncToAws ?? form.syncToAws,
    };
    lastCommittedNextSteps = String(form.nextSteps ?? "");
    loadedFormSnapshot = JSON.parse(JSON.stringify(form));
  };

  const handleOpenOpportunityPopup = async ({ id }: { id?: number } = {}): Promise<void> => {
    resetForm();
    showForm = true;

    const opportunityId = id ?? 0;
    if (!(opportunityId > 0)) {
      setPopupError("Missing opportunity id.");
      return;
    }
    currentOpportunityId = opportunityId;

    try {
      const res = await fetch(`${baseUrl}/local/${opportunityId}`);
      if (!res.ok) {
        setPopupError(`Failed to load opportunity ${opportunityId}.`);
        return;
      }

      const data = await res.json();
      if (data?.success === false) {
        setPopupError(data?.response?.error || `Failed to load opportunity ${opportunityId}.`);
        return;
      }

      applyLocalPayloadToForm(data?.payload);
      awsOppId = String(data?.payload?.awsOppId ?? "").trim();

      if (awsOppId) {
        await fetchAwsOpportunity();
      }
    } catch (err: unknown) {
      setPopupError(err instanceof Error ? err.message : "Failed to load opportunity.");
    }
  };

  const handleFillMandatoryFields = async (): Promise<void> => {
    isFillingMandatoryFields = true;
    apiError = [];
    fillSummaryMessages = [];
    showMandatoryFillPanel = true;
    ensurePartnerCrmId();
    const abortController = new AbortController();
    const abortTimer = setTimeout(() => abortController.abort(), 150_000);
    try {
      const opportunity = buildSyncPayload(
        form.partnerCrmUniqueId ?? "",
        getReviewStatusForSubmit(),
        normalizeNextStepsHistory(form.nextStepsHistory)
      );
      const fillRes = await fetch(
        currentOpportunityId
          ? `${baseUrl}/${currentOpportunityId}/mandatory-field-fill`
          : `${baseUrl}/mandatory-field-fill-preview`,
        {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        signal: abortController.signal,
        body: JSON.stringify({
          dryRun: true,
          opportunity,
          context: form.mandatoryFieldContext || undefined,
        }),
        }
      );
      const fillData = await fillRes.json().catch(() => ({}));
      if (!fillRes.ok || fillData?.success === false) {
        apiError = [fillData?.message || "Failed to fill AWS mandatory fields."];
        await scrollToValidationSummary();
        return;
      }
      const fillResult = applyMandatoryFieldFillToForm(
        form,
        createDefaultForm(),
        fillData?.applied ?? fillData?.filled
      );
      form = fillResult.form;
      const validationErrors = await evaluateOpportunityFormRules(
        normalizeNextStepsHistory(form.nextStepsHistory)
      );
      errors = validationErrors;
      fillSummaryMessages = buildMandatoryFieldFillSummaryMessages(
        fillResult.applied,
        fillResult.skipped,
        fillData,
        Object.keys(validationErrors).filter((field) => field !== "_general")
      );
      showMandatoryFillPanel = true;
    } catch (error) {
      console.error("Failed to fill AWS mandatory fields", error);
      apiError = [
        abortController.signal.aborted
          ? "The AI fill request took too long and was cancelled. Please try again."
          : "Failed to fill AWS mandatory fields.",
      ];
      await scrollToValidationSummary();
    } finally {
      clearTimeout(abortTimer);
      isFillingMandatoryFields = false;
    }
  };

  const evaluateOpportunityFormRules = async (
    nextStepsHistory: { Time: string; Value: string }[]
  ): Promise<Record<string, string>> => {
    const reviewStatusForSubmit = getReviewStatusForSubmit();
    let nextErrors: Record<string, string> = {};

    const ruleResult = await ruleEvaluator.evaluate({
      ...form,
      // Map the legacy field to the corrected rule fact.
      estimatedAwsMonthlyRecurringRevenue: form.estimatedAwsMonthlyReccuringRevenue,
      nextStepsHistory,
    });
    if (!ruleResult.success) {
      nextErrors = { ...nextErrors, ...ruleResult.errors };
    }

    const lifecycleFacts = {
      ...form,
      stage: form.awsStage ?? (awsOppId ? "Qualified" : "Prospect"),
      status: reviewStatusForSubmit ?? "",
      reviewStatus: reviewStatusForSubmit ?? "",
      catalog: isAwsCatalog(awsCatalog) ? "AWS" : "Sandbox",
      targetCloseDate: form.targetCloseDate,
      solutionOffered: Array.isArray(form.solutionOffered)
        ? (() => {
            const selectedSolutions = getEffectiveSolutionSelections(form.solutionOffered);
            return selectedSolutions.includes("__other__") || selectedSolutions.includes("Other")
              ? "Other"
              : (selectedSolutions[0] ?? "");
          })()
        : "",
      otherSolutionOffered: form.otherSolOffered,
      awsAccountId: form.awsAccountId,
      closedLostReason: form.closedLostReason,
      nextSteps: form.nextSteps,
      nextStepsHistory,
    };

    const lifecycleResult = await lifecycleRuleEvaluator.evaluate(lifecycleFacts);
    if (!lifecycleResult.success) {
      const mappedErrors = { ...(lifecycleResult.errors as Record<string, string>) };
      if (mappedErrors.otherSolutionOffered) {
        mappedErrors.otherSolOffered = mappedErrors.otherSolutionOffered;
        delete mappedErrors.otherSolutionOffered;
      }
      nextErrors = { ...nextErrors, ...mappedErrors };
    }

    const reviewStatusResult = await reviewStatusRuleEvaluator.evaluate(lifecycleFacts);
    if (!reviewStatusResult.success) {
      const mappedErrors = { ...(reviewStatusResult.errors as Record<string, string>) };
      if (mappedErrors.status) {
        mappedErrors.reviewStatus = mappedErrors.status;
        delete mappedErrors.status;
      }
      nextErrors = { ...nextErrors, ...mappedErrors };
    }

    return nextErrors;
  };

  const handleMarketingSourceChange = (value: string): void => {
    form.marketingSource = value
      ? (value as LocalOpportunityPayload["marketingSource"])
      : undefined;
  };

  const addItem = (field: keyof LocalOpportunityPayload, emptyValue: any): void => {
    (form[field] as any[]) = [...((form[field] as any[]) || []), emptyValue];
  };

  const removeItem = (field: keyof LocalOpportunityPayload, id: number): void => {
    (form[field] as any[]) = (form[field] as any[]).filter((_, i) => i !== id);
  };

  const openForm = (): void => {
    resetForm();
    showForm = true;
  };

  const getSolutionValue = (solution: { Id: string }): string => solution.Id;
  const getSolutionOptionLabel = (solution: { Id: string; Name: string }): string =>
    `${solution.Name} (${solution.Id})`;
  const getSolutionBadgeLabel = (solution: { Name: string }): string => solution.Name;

  const getProductValue = (product: { Identifier: string }): string => product.Identifier;
  const getProductOptionLabel = (product: { Identifier: string; Name: string }): string =>
    `${product.Name} (${product.Identifier})`;
  const getProductBadgeLabel = (product: { Name: string }): string => product.Name;

  const fetchSolutions = async (): Promise<void> => {
    try {
      const res = await fetch(`${DEFAULT_URL}/api/aws-partner-central/list-solutions`, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });
      const data = await res.json();
      solutions = data.SolutionSummaries ?? [];
    } catch (e) {
      console.error("Failed to load solutions", e);
    }
  };

  const fetchProducts = async (): Promise<void> => {
    try {
      const res = await fetch(`${DEFAULT_URL}/api/aws-partner-central/products`);
      if (!res.ok) throw new Error("Failed to load AWS products");
      products = await res.json();
    } catch (e) {
      console.error("Failed to load products", e);
    }
  };

  const fetchUseCases = async (): Promise<void> => {
    try {
      const res = await fetch(`${DEFAULT_URL}/api/sync/use-cases`);
      if (!res.ok) throw new Error("Failed to load Use Cases");
      useCaseOptions = await res.json();
    } catch (e) {
      console.error("Failed to load Use Cases", e);
    }
  };

  const fetchCurrencyCodes = async (): Promise<void> => {
    try {
      const res = await fetch(`${process.env.SVELTE_APP_ALADV_BASE_URL}/aladv/currency-codes`);
      if (!res.ok) throw new Error("Failed to load currency codes");
      const data = await res.json();
      currencyCodes = Array.isArray(data)
        ? data.map((item: any) => item?.code).filter(Boolean)
        : [];
    } catch (e) {
      console.error("Failed to load currency codes", e);
    }
  };

  const fetchCountries = async (): Promise<void> => {
    try {
      const res = await fetch(`${DEFAULT_URL}/api/sync/countries`);
      if (!res.ok) throw new Error("Failed to load countries");
      countries = await res.json();
    } catch (e) {
      console.error("Failed to load countries", e);
    }
  };

  const fetchUsStates = async (): Promise<void> => {
    try {
      const res = await fetch(`${DEFAULT_URL}/api/sync/us-states`);
      if (!res.ok) throw new Error("Failed to load Use Cases");
      usStates = await res.json();
    } catch (e) {
      console.error("Failed to load Use Cases", e);
    }
  };

  const fetchPostalCodeRules = async (): Promise<void> => {
    try {
      const res = await fetch(`${DEFAULT_URL}/api/sync/postal-code-rules`);
      if (!res.ok) throw new Error("Failed to load postal code rules");

      const data: Record<string, string> = await res.json();
      // Convert each regex string to a RegExp object
      postalCodeRules = Object.fromEntries(
        Object.entries(data).map(([country, pattern]) => [country, new RegExp(pattern)])
      );
    } catch (e) {
      console.error("Failed to load postal code rules", e);
    }
  };

  const fetchAwsCatalog = async (): Promise<void> => {
    try {
      const res = await fetch(SETTINGS_URL);
      if (!res.ok) throw new Error("Failed to load AWS catalog settings");
      const data = await res.json();
      awsCatalog = data?.awsCatalog || "sandbox";
    } catch (e) {
      console.error("Failed to load AWS catalog settings", e);
      awsCatalog = "sandbox";
    }
  };

  // Load dropdown options
  onMount(async () => {
    eventBus.on("open-opportunity-popup", handleOpenOpportunityPopup);
    try {
      await Promise.all([
        opportunityOptions.fetchOptions(),
        fetchSolutions(),
        fetchProducts(),
        fetchUseCases(),
        fetchCountries(),
        fetchCurrencyCodes(),
        fetchUsStates(),
        fetchPostalCodeRules(),
        fetchAwsCatalog(),
      ]);
    } catch (err: any) {
      apiError = err.message ?? "Unexpected error occurred while fetching data.";
      console.error("Initialization error:", err);
    }
  });

  onDestroy(() => {
    eventBus.off("open-opportunity-popup", handleOpenOpportunityPopup);
  });

  const AWS_LOCKED_STATUSES = new Set(["Submitted", "In review"]);

  const withCurrentOption = (list: string[], current?: string): string[] =>
    current && !list.includes(current) ? [current, ...list] : list;

  $: options = $opportunityOptions.data;
  $: currencyCodeOptions = withCurrentOption(
    currencyCodes.length
      ? currencyCodes
      : options?.currencyCode?.length
        ? options.currencyCode
        : [DEFAULT_EXPECTED_CUSTOMER_SPEND_CURRENCY_CODE],
    form.expectedCustomerSpendCurrencyCode
  );
  $: expectedSpendFrequencyOptions = withCurrentOption(
    EXPECTED_CUSTOMER_SPEND_FREQUENCY_OPTIONS,
    form.expectedCustomerSpendFrequency
  );
  $: expectedSpendTargetCompanyOptions = withCurrentOption(
    EXPECTED_CUSTOMER_SPEND_TARGET_COMPANY_OPTIONS,
    form.expectedCustomerSpendTargetCompany
  );
  // CurrencyCode, Frequency and TargetCompany are required by AWS, so they always carry a value.
  $: if (!form.expectedCustomerSpendCurrencyCode) {
    form.expectedCustomerSpendCurrencyCode = DEFAULT_EXPECTED_CUSTOMER_SPEND_CURRENCY_CODE;
  }
  $: if (!form.expectedCustomerSpendFrequency) {
    form.expectedCustomerSpendFrequency = DEFAULT_EXPECTED_CUSTOMER_SPEND_FREQUENCY;
  }
  $: if (!form.expectedCustomerSpendTargetCompany) {
    form.expectedCustomerSpendTargetCompany = DEFAULT_EXPECTED_CUSTOMER_SPEND_TARGET_COMPANY;
  }
  $: isUpdateMode = Boolean(awsOppId && awsOppId.trim() !== "");
  // Origin is locked to Partner Referral; AWS Referral records only arrive via accepted engagement invitations.
  $: originOptions = withCurrentOption([DEFAULT_ORIGIN], form.origin);
  $: if (!isUpdateMode && form.origin !== DEFAULT_ORIGIN) {
    form.origin = DEFAULT_ORIGIN;
  }
  $: if (!form.origin) {
    form.origin = DEFAULT_ORIGIN;
  }
  $: editRestrictionNote = ((): { title: string; detail: string } | null => {
    if (!isUpdateMode) return null;
    const status = savedAwsReviewStatus.trim();
    if (status === "Action Required") {
      return {
        title: "AWS requested changes (Action Required)",
        detail:
          "Fields marked with a lock keep their value saved at AWS; the remaining fields can be edited and resubmitted.",
      };
    }
    if (status === "Approved") {
      return {
        title: "Approved by AWS",
        detail: "Fields marked with a lock can no longer be changed after approval.",
      };
    }
    if (status === "Rejected") {
      return {
        title: "Rejected by AWS",
        detail:
          "AWS no longer accepts changes to this opportunity. To make changes, select a different AWS Review Status and save.",
      };
    }
    if (AWS_LOCKED_STATUSES.has(status)) {
      return {
        title: `Under AWS review (${status})`,
        detail:
          "All fields are locked while AWS reviews this opportunity. To make changes, select a different AWS Review Status and save.",
      };
    }
    return null;
  })();
  $: isReviewStatusEditable = true;
  $: {
    const currentStatus = typeof form.reviewStatus === "string" ? form.reviewStatus.trim() : "";

    if (!isUpdateMode) {
      reviewStatusSelectOptions = [
        { value: CREATE_REVIEW_STATUS, label: CREATE_REVIEW_STATUS, disabled: true },
        ...updateAllowedReviewStatuses
          .filter((status) => status !== CREATE_REVIEW_STATUS)
          .map((status) => ({
            value: status,
            label: status,
            disabled: false,
          })),
      ];
    } else {
      const updateOptions = updateAllowedReviewStatuses.map((status) => ({
        value: status,
        label: status,
        disabled: false,
      }));

      if (currentStatus && !updateAllowedReviewStatusSet.has(currentStatus)) {
        updateOptions.unshift({
          value: currentStatus,
          label: `${currentStatus} (AWS read-only)`,
          disabled: true,
        });
      }

      reviewStatusSelectOptions = updateOptions;
    }
  }
  $: nextStepsHistoryItems = normalizeNextStepsHistory(form.nextStepsHistory);
</script>

<button
  class="px-4 py-2 text-sm font-semibold bg-primary-600 text-white rounded-lg shadow hover:bg-primary-700 transition"
  on:click={() => openForm()}
>
  + Create / Update Opportunity
</button>

{#if showForm}
  <div class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div
      class="opportunity-popup-panel rounded-xl shadow-strong w-11/12 overflow-y-auto p-6 relative"
      style={`max-height:90vh; ${themeCssVars}`}
    >
      <!-- Header -->
      <div class="flex items-center justify-between mb-6 border-b border-white/10 pb-3">
        <h2 class="text-xl font-bold text-white">
          {awsOppId ? "Edit Opportunity" : "Create Opportunity"}
        </h2>
        <button
          class="p-1 rounded-full text-white/60 hover:text-white hover:bg-white/10 transition"
          aria-label="Close"
          on:click={() => (showForm = false)}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-5 w-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            stroke-width="2"
          >
            <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {#if editRestrictionNote}
        <div class="mb-4 flex items-start gap-3 rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-3">
          <svg class="w-5 h-5 text-amber-400 shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
            <path
              fill-rule="evenodd"
              d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
              clip-rule="evenodd"
            />
          </svg>
          <div class="text-sm text-amber-200">
            <p class="font-semibold text-amber-100">{editRestrictionNote.title}</p>
            <p class="mt-0.5">{editRestrictionNote.detail}</p>
          </div>
        </div>
      {/if}

      <div class="space-y-8">
        <!-- AWS Opportunity -->
        <section>
          <h3 class="font-semibold mb-3">AWS Opportunity ID (Optional)</h3>
          <div class="flex gap-2">
            <input placeholder="AWS Opportunity ID" bind:value={awsOppId} class="input flex-1" />
            <button
              type="button"
              class="popup-secondary-button px-4 py-2 rounded-lg"
              on:click={fetchAwsOpportunity}
              disabled={isSaving}
            >
              {isSaving ? "Fetching..." : "Fetch"}
            </button>
          </div>
        </section>

        <!-- Core -->
        <section>
          <h3 class="font-semibold mb-3">Core</h3>
          <div class="grid md:grid-cols-2 gap-4">
            <FieldLabel
              label="Opportunity Type"
              locked={!canEditAwsField("opportunityType", reviewStatusForFieldLock)}
            >
              <select
                value={form.opportunityType || ""}
                disabled={!canEditAwsField("opportunityType", reviewStatusForFieldLock)}
                on:change={(event) => (form.opportunityType = event.currentTarget.value)}
                class="input"
              >
                <option value="">-- Select Opportunity Type --</option>
                {#each options?.opportunityType ?? [] as opt}
                  <option value={opt}>{opt}</option>
                {/each}
              </select>
              {#if errors.opportunityType}
                <p class="text-red-600 text-sm">{errors.opportunityType}</p>
              {/if}
            </FieldLabel>
            <FieldLabel
              label="Parent Opportunity ID"
              locked={!canEditAwsField("parentOppId", reviewStatusForFieldLock)}
            >
              <input
                placeholder="Parent Opportunity ID"
                bind:value={form.parentOppId}
                class="input"
                disabled={!canEditAwsField("parentOppId", reviewStatusForFieldLock)}
              />
            </FieldLabel>
            <FieldLabel label="Partner CRM Unique ID (auto-generated)">
              <input
                placeholder="Partner CRM Unique ID"
                value={form.partnerCrmUniqueId}
                disabled
                class="input"
              />
            </FieldLabel>
            <FieldLabel label="Origin">
              <select bind:value={form.origin} disabled class="input">
                {#each originOptions as opt}
                  <option value={opt}>{opt}</option>
                {/each}
              </select>
              {#if errors.origin}
                <p class="text-red-600 text-sm">{errors.origin}</p>
              {/if}
            </FieldLabel>
          </div>
        </section>

        <!-- Customer -->
        <section>
          <h3 class="font-semibold mb-3">Customer</h3>
          <div class="grid md:grid-cols-2 gap-4">
            <FieldLabel
              label="Customer Company Name"
              locked={!canEditAwsField("customerCompanyName", reviewStatusForFieldLock)}
            >
              <input
                placeholder="Customer Company Name"
                bind:value={form.customerCompanyName}
                class="input"
                disabled={!canEditAwsField("customerCompanyName", reviewStatusForFieldLock)}
              />
              {#if errors.customerCompanyName}<p class="text-red-600 text-sm">
                  {errors.customerCompanyName}
                </p>{/if}
            </FieldLabel>
            <FieldLabel
              label="Customer DUNS"
              locked={!canEditAwsField("customerDuns", reviewStatusForFieldLock)}
            >
              <input placeholder="Customer DUNS" bind:value={form.customerDuns} class="input" disabled={!canEditAwsField("customerDuns", reviewStatusForFieldLock)} />
              {#if errors.customerDuns}
                <p class="text-red-600 text-sm">{errors.customerDuns}</p>
              {/if}
            </FieldLabel>
            <FieldLabel
              label="Industry Vertical"
              locked={!canEditAwsField("industryVertical", reviewStatusForFieldLock)}
            >
              <select
                value={form.industryVertical || ""}
                disabled={!canEditAwsField("industryVertical", reviewStatusForFieldLock)}
                on:change={(event) => (form.industryVertical = event.currentTarget.value)}
                class="input"
              >
                <option value="">-- Select Industry Vertical --</option>
                {#each options?.industryVertical ?? [] as opt}
                  <option value={opt}>{opt}</option>
                {/each}
              </select>
              {#if errors.industryVertical}
                <p class="text-red-600 text-sm">{errors.industryVertical}</p>
              {/if}
            </FieldLabel>
            {#if form.industryVertical === "Other"}
              <FieldLabel
                label="Other Industry"
                locked={!canEditAwsField("industryOther", reviewStatusForFieldLock)}
              >
                <input placeholder="Other Industry" bind:value={form.industryOther} class="input" disabled={!canEditAwsField("industryOther", reviewStatusForFieldLock)} />
                {#if errors.industryOther}<p class="text-red-600 text-sm">
                    {errors.industryOther}
                  </p>{/if}
              </FieldLabel>
            {/if}
            <FieldLabel label="Website" locked={!canEditAwsField("website", reviewStatusForFieldLock)}>
              <input placeholder="Website" bind:value={form.website} class="input" disabled={!canEditAwsField("website", reviewStatusForFieldLock)} />
              {#if errors.website}<p class="text-red-600 text-sm">{errors.website}</p>{/if}
            </FieldLabel>
            <FieldLabel
              label="AWS Account ID"
              locked={!canEditAwsField("awsAccountId", reviewStatusForFieldLock)}
            >
              <input placeholder="AWS Account ID" bind:value={form.awsAccountId} class="input" disabled={!canEditAwsField("awsAccountId", reviewStatusForFieldLock)} />
              {#if errors.awsAccountId}<p class="text-red-600 text-sm">{errors.awsAccountId}</p>{/if}
            </FieldLabel>
          </div>
        </section>

        <!-- Address -->
        <section>
          <h3 class="font-semibold mb-3">Address</h3>
          <div class="grid md:grid-cols-2 gap-4">
            <FieldLabel
              label="Street Address"
              locked={!canEditAwsField("streetAddress", reviewStatusForFieldLock)}
            >
              <input placeholder="Street Address" bind:value={form.streetAddress} class="input" disabled={!canEditAwsField("streetAddress", reviewStatusForFieldLock)} />
            </FieldLabel>
            <FieldLabel label="City" locked={!canEditAwsField("city", reviewStatusForFieldLock)}>
              <input placeholder="City" bind:value={form.city} class="input" disabled={!canEditAwsField("city", reviewStatusForFieldLock)} />
            </FieldLabel>

            {#if form.country === "US"}
              <FieldLabel
                label="State / Province"
                locked={!canEditAwsField("state", reviewStatusForFieldLock)}
              >
                <select
                  value={form.state || ""}
                  disabled={!canEditAwsField("state", reviewStatusForFieldLock)}
                  on:change={(event) => (form.state = event.currentTarget.value)}
                  class="input"
                >
                  <option value="">-- Select State / Province --</option>
                  {#each usStates as st}
                    <option value={st}>{st}</option>
                  {/each}
                </select>
                {#if errors.state}
                  <p class="text-red-600 text-sm">{errors.state}</p>
                {/if}
              </FieldLabel>
            {/if}

            <FieldLabel
              label="Postal Code"
              locked={!canEditAwsField("postalCode", reviewStatusForFieldLock)}
            >
              <input placeholder="Postal Code" bind:value={form.postalCode} class="input" disabled={!canEditAwsField("postalCode", reviewStatusForFieldLock)} />
              {#if errors.postalCode}
                <p class="text-red-600 text-sm">{errors.postalCode}</p>
              {/if}
            </FieldLabel>

            <FieldLabel label="Country" locked={!canEditAwsField("country", reviewStatusForFieldLock)}>
              <select
                value={form.country || ""}
                disabled={!canEditAwsField("country", reviewStatusForFieldLock)}
                on:change={(event) => (form.country = event.currentTarget.value)}
                class="input"
              >
                <option value="">-- Select Country --</option>
                {#each countries as c}
                  <option value={c.value}>{c.label}</option>
                {/each}
              </select>
              {#if errors.country}
                <p class="text-red-600 text-sm">{errors.country}</p>
              {/if}
            </FieldLabel>
          </div>
        </section>

        <!-- Contacts -->
        <section>
          <h3 class="font-semibold mb-3 flex justify-between items-center">
            <span class="flex items-center gap-1.5">
              Customer Contacts
              <AwsLockIcon locked={!canEditAwsField("customerContacts", reviewStatusForFieldLock)} />
            </span>
            <button
              class="text-primary-600 hover:underline disabled:text-gray-400 disabled:no-underline"
              type="button"
              disabled={!canEditAwsField("customerContacts", reviewStatusForFieldLock)}
              on:click={() =>
                addItem("customerContacts", {
                  firstName: "",
                  lastName: "",
                  email: "",
                  businessTitle: "",
                  phone: "",
                })}>+ Add</button
            >
          </h3>
          {#each form.customerContacts as contact, id}
            <div class="ace-item-card p-6 rounded-lg mb-3 relative">
              <button
                class="ace-remove-btn absolute top-2 right-3 p-1 rounded-full transition"
                aria-label="Remove contact"
                disabled={!canEditAwsField("customerContacts", reviewStatusForFieldLock)}
                on:click={() => removeItem("customerContacts", id)}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              <div class="grid md:grid-cols-2 gap-3">
                <FieldLabel label="First Name">
                  <input placeholder="First Name" bind:value={contact.firstName} class="input" disabled={!canEditAwsField("customerContacts", reviewStatusForFieldLock)} />
                  {#if errors[`contact-${id}-firstName`]}
                    <p class="text-red-600 text-sm">{errors[`contact-${id}-firstName`]}</p>
                  {/if}
                </FieldLabel>
                <FieldLabel label="Last Name">
                  <input placeholder="Last Name" bind:value={contact.lastName} class="input" disabled={!canEditAwsField("customerContacts", reviewStatusForFieldLock)} />
                  {#if errors[`contact-${id}-lastName`]}
                    <p class="text-red-600 text-sm">{errors[`contact-${id}-lastName`]}</p>
                  {/if}
                </FieldLabel>
                <FieldLabel label="Email">
                  <input placeholder="Email" bind:value={contact.email} class="input" disabled={!canEditAwsField("customerContacts", reviewStatusForFieldLock)} />
                  {#if errors[`contact-${id}-email`]}
                    <p class="text-red-600 text-sm">{errors[`contact-${id}-email`]}</p>
                  {/if}
                </FieldLabel>
                {#if errors[`contact-${id}`]}<p class="text-red-600 text-sm">
                    {errors[`contact-${id}`]}
                  </p>{/if}
                <FieldLabel label="Business Title">
                  <input
                    placeholder="Business Title"
                    bind:value={contact.businessTitle}
                    class="input"
                    disabled={!canEditAwsField("customerContacts", reviewStatusForFieldLock)}
                  />
                  {#if errors[`contact-${id}-businessTitle`]}
                    <p class="text-red-600 text-sm">{errors[`contact-${id}-businessTitle`]}</p>
                  {/if}
                </FieldLabel>
                <FieldLabel label="Phone">
                  <input placeholder="Phone" bind:value={contact.phone} class="input" disabled={!canEditAwsField("customerContacts", reviewStatusForFieldLock)} />
                  {#if errors[`contact-${id}-phone`]}
                    <p class="text-red-600 text-sm">{errors[`contact-${id}-phone`]}</p>
                  {/if}
                </FieldLabel>
              </div>
            </div>
          {/each}
        </section>

        <!-- Project -->
        <section>
          <h3 class="font-semibold mb-3">Project</h3>
          <div class="space-y-3">
            <FieldLabel
              label="Project Title"
              locked={!canEditAwsField("partnerProjectTitle", reviewStatusForFieldLock)}
            >
              <input
                placeholder="Project Title"
                bind:value={form.partnerProjectTitle}
                disabled={!canEditAwsField("partnerProjectTitle", reviewStatusForFieldLock)}
                class="input"
              />
              {#if errors.partnerProjectTitle}<p class="text-red-600 text-sm">
                  {errors.partnerProjectTitle}
                </p>{/if}
            </FieldLabel>
            <FieldLabel
              label="Customer Business Problem"
              locked={!canEditAwsField("customerBusinessProblem", reviewStatusForFieldLock)}
            >
              <textarea
                placeholder="Customer Business Problem"
                bind:value={form.customerBusinessProblem}
                class="input"
                disabled={!canEditAwsField("customerBusinessProblem", reviewStatusForFieldLock)}
              />
              {#if errors.customerBusinessProblem}<p class="text-red-600 text-sm">
                  {errors.customerBusinessProblem}
                </p>{/if}
            </FieldLabel>
            <section>
              <h3 class="font-semibold mb-3 flex items-center gap-1.5">
                Solution Offered
                <AwsLockIcon locked={!canEditAwsField("solutionOffered", reviewStatusForFieldLock)} />
              </h3>
              <MultiCheckboxDropdown
                items={solutions}
                bind:selectedValues={form.solutionOffered}
                placeholder="Select solutions..."
                getItemValue={getSolutionValue}
                getItemLabel={getSolutionOptionLabel}
                getBadgeLabel={getSolutionBadgeLabel}
                otherOption={{ value: "__other__", label: "Other", exclusive: true }}
                disabled={!canEditAwsField("solutionOffered", reviewStatusForFieldLock)}
              />

              {#if errors.solutionOffered}
                <p class="text-red-600 text-sm mt-2">{errors.solutionOffered}</p>
              {/if}

              <!-- Show text input if "Other" is selected -->
              {#if form.solutionOffered.includes("__other__")}
                <input
                  placeholder="Other Solution Offered"
                  bind:value={form.otherSolOffered}
                  class="input mt-2"
                  disabled={!canEditAwsField("otherSolOffered", reviewStatusForFieldLock)}
                />
                {#if errors.otherSolOffered}
                  <p class="text-red-600 text-sm">{errors.otherSolOffered}</p>
                {/if}
              {/if}
            </section>

            <section>
              <FieldLabel label="Use Case" locked={!canEditAwsField("useCase", reviewStatusForFieldLock)}>
                <select
                  value={form.useCase || ""}
                  disabled={!canEditAwsField("useCase", reviewStatusForFieldLock)}
                  on:change={(event) => (form.useCase = event.currentTarget.value)}
                  class="input"
                >
                  <option value="">-- Select Use Case --</option>
                  {#each useCaseOptions as opt}
                    <option value={opt}>{opt}</option>
                  {/each}
                </select>
                {#if errors.useCase}
                  <p class="text-red-600 text-sm">{errors.useCase}</p>
                {/if}
              </FieldLabel>
            </section>

            <section>
              <FieldLabel
                label="Estimated AWS Monthly Recurring Revenue"
                locked={!canEditAwsField("estimatedAwsMonthlyReccuringRevenue", reviewStatusForFieldLock)}
              >
                <input
                  type="number"
                  min="0"
                  step="1"
                  inputmode="numeric"
                  placeholder="Estimated AWS Monthly Recurring Revenue"
                  bind:value={form.estimatedAwsMonthlyReccuringRevenue}
                  disabled={!canEditAwsField("estimatedAwsMonthlyReccuringRevenue", reviewStatusForFieldLock)}
                  on:input={handleEstimatedAwsMonthlyReccuringRevenueInput}
                  class="input"
                />
                {#if errors.estimatedAwsMonthlyReccuringRevenue}
                  <p class="text-red-600 text-sm">{errors.estimatedAwsMonthlyReccuringRevenue}</p>
                {/if}
              </FieldLabel>
            </section>

            <section>
              <h3 class="font-semibold mb-3">Expected Customer Spend</h3>
              <div class="grid md:grid-cols-2 gap-4">
                <FieldLabel
                  label="Currency Code"
                  locked={!canEditAwsField("expectedCustomerSpendCurrencyCode", reviewStatusForFieldLock)}
                >
                  <select
                    bind:value={form.expectedCustomerSpendCurrencyCode}
                    disabled={!canEditAwsField("expectedCustomerSpendCurrencyCode", reviewStatusForFieldLock)}
                    class="input"
                  >
                    {#each currencyCodeOptions as code}
                      <option value={code}>{code}</option>
                    {/each}
                  </select>
                  {#if errors.expectedCustomerSpendCurrencyCode}
                    <p class="text-red-600 text-sm">{errors.expectedCustomerSpendCurrencyCode}</p>
                  {/if}
                </FieldLabel>
                <!-- AWS only accepts Frequency: Monthly and TargetCompany: AWS. -->
                <FieldLabel label="Frequency">
                  <select bind:value={form.expectedCustomerSpendFrequency} disabled class="input">
                    {#each expectedSpendFrequencyOptions as opt}
                      <option value={opt}>{opt}</option>
                    {/each}
                  </select>
                  {#if errors.expectedCustomerSpendFrequency}
                    <p class="text-red-600 text-sm">{errors.expectedCustomerSpendFrequency}</p>
                  {/if}
                </FieldLabel>
                <FieldLabel label="Target Company">
                  <select bind:value={form.expectedCustomerSpendTargetCompany} disabled class="input">
                    {#each expectedSpendTargetCompanyOptions as opt}
                      <option value={opt}>{opt}</option>
                    {/each}
                  </select>
                  {#if errors.expectedCustomerSpendTargetCompany}
                    <p class="text-red-600 text-sm">{errors.expectedCustomerSpendTargetCompany}</p>
                  {/if}
                </FieldLabel>
              </div>
            </section>

            <FieldLabel
              label="Additional Comments"
              locked={!canEditAwsField("additionalComments", reviewStatusForFieldLock)}
            >
              <textarea
                placeholder="Additional Comments"
                bind:value={form.additionalComments}
                class="input"
                disabled={!canEditAwsField("additionalComments", reviewStatusForFieldLock)}
              />
            </FieldLabel>
          </div>
        </section>

        <!-- Lifecycle -->
        <section>
          <h3 class="font-semibold mb-3 flex items-center gap-1.5">
            Target Close Date
            <AwsLockIcon locked={!canEditAwsField("targetCloseDate", reviewStatusForFieldLock)} />
          </h3>
          <input type="date" bind:value={form.targetCloseDate} class="input" disabled={!canEditAwsField("targetCloseDate", reviewStatusForFieldLock)} />
          {#if errors.targetCloseDate}<p class="text-red-600 text-sm">
              {errors.targetCloseDate}
            </p>{/if}
        </section>

        <section>
          <h3 class="font-semibold mb-3 flex items-center gap-1.5">
            AWS Stage
            <AwsLockIcon locked={!canEditAwsField("awsStage", reviewStatusForFieldLock)} />
          </h3>
          <select bind:value={form.awsStage} class="input" disabled={!canEditAwsField("awsStage", reviewStatusForFieldLock)}>
            <option value="">-- Select AWS Stage --</option>
            {#each (options?.stages) as stage}
              <option value={stage.name}>{stage.name}</option>
            {/each}
          </select>
          {#if errors.awsStage}
            <p class="text-red-600 text-sm">{errors.awsStage}</p>
          {/if}
        </section>

        {#if form.awsStage === "Closed Lost"}
          <section>
            <FieldLabel
              label="Closed Lost Reason"
              locked={!canEditAwsField("closedLostReason", reviewStatusForFieldLock)}
            >
              <select
                value={form.closedLostReason || ""}
                disabled={!canEditAwsField("closedLostReason", reviewStatusForFieldLock)}
                on:change={(event) => (form.closedLostReason = event.currentTarget.value)}
                class="input"
              >
                <option value="">-- Select Closed Lost Reason --</option>
                {#each (options?.closedLostReason ?? []) as reason}
                  <option value={reason}>{reason}</option>
                {/each}
              </select>
              {#if errors.closedLostReason}
                <p class="text-red-600 text-sm">{errors.closedLostReason}</p>
              {/if}
            </FieldLabel>
          </section>
        {/if}

        <section>
          <h3 class="font-semibold mb-3">AWS Review Status</h3>
          <select bind:value={form.reviewStatus} class="input" disabled={!isReviewStatusEditable}>
            <option value="">-- Select AWS Review Status --</option>
            {#each reviewStatusSelectOptions as status}
              <option value={status.value} disabled={status.disabled}>{status.label}</option>
            {/each}
          </select>
          {#if errors.reviewStatus}
            <p class="text-red-600 text-sm">{errors.reviewStatus}</p>
          {/if}
        </section>

        <section>
          <h3 class="font-semibold mb-3 flex items-center gap-1.5">
            Next Steps
            <AwsLockIcon locked={!canEditAwsField("nextSteps", reviewStatusForFieldLock)} />
          </h3>
          <textarea
            placeholder="Next Steps"
            bind:value={form.nextSteps}
            class="input"
            disabled={!canEditAwsField("nextSteps", reviewStatusForFieldLock)}
          />
          {#if errors.nextSteps}
            <p class="text-red-600 text-sm">{errors.nextSteps}</p>
          {/if}

          <div class="mt-3">
            <div class="flex items-center justify-between mb-2">
              <h4 class="font-medium">Next Steps History</h4>
              <button
                type="button"
                class="text-sm text-primary-600 hover:underline"
                on:click={() => (showNextStepsHistory = !showNextStepsHistory)}
              >
                {showNextStepsHistory
                  ? "Hide history"
                  : `Show history (${nextStepsHistoryItems.length})`}
              </button>
            </div>
            {#if showNextStepsHistory}
              {#if nextStepsHistoryItems.length === 0}
                <p class="text-sm text-secondary-500">No history yet.</p>
              {:else}
                <div class="history-panel ace-item-card rounded-lg p-3 max-h-[220px] overflow-y-auto space-y-3">
                  {#each nextStepsHistoryItems as item}
                    <div class="text-sm">
                      <p class="text-xs text-secondary-500 font-mono break-all">
                        {formatHistoryTime(item?.Time)}
                      </p>
                      <p class="whitespace-pre-wrap">{item?.Value ?? ""}</p>
                    </div>
                  {/each}
                </div>
              {/if}
            {/if}
            {#if errors.nextStepsHistory}
              <p class="text-red-600 text-sm">{errors.nextStepsHistory}</p>
            {/if}
            <p class="text-xs text-secondary-500 mt-1">
              Automatically stores previous Next Steps values (max {nextStepsHistoryMaxItems} items).
            </p>
          </div>
        </section>

        <section>
          <h3 class="font-semibold mb-3 flex items-center gap-1.5">
            Competitive Tracking
            <AwsLockIcon locked={!canEditAwsField("competitiveTracking", reviewStatusForFieldLock)} />
          </h3>
          <select
            value={form.competitiveTracking || ""}
            disabled={!canEditAwsField("competitiveTracking", reviewStatusForFieldLock)}
            on:change={(event) => (form.competitiveTracking = event.currentTarget.value)}
            class="input"
          >
            <option value="">-- Select --</option>
            {#each options.competitorName ?? [] as opt}
              <option value={opt}>{opt}</option>
            {/each}
          </select>
          {#if errors.competitiveTracking}
            <p class="text-red-600 text-sm">{errors.competitiveTracking}</p>
          {/if}

          {#if form.competitiveTracking === "*Other"}
            <div class="mt-2">
              <FieldLabel
                label="Other Competitors"
                locked={!canEditAwsField("otherCompetitors", reviewStatusForFieldLock)}
              >
                <input
                  placeholder="Other Competitors"
                  bind:value={form.otherCompetitors}
                  class="input"
                  disabled={!canEditAwsField("otherCompetitors", reviewStatusForFieldLock)}
                />
                {#if errors.otherCompetitors}
                  <p class="text-red-600 text-sm">{errors.otherCompetitors}</p>
                {/if}
              </FieldLabel>
            </div>
          {/if}
        </section>

        <!-- Marketing -->
        <section>
          <h3 class="font-semibold mb-3">Marketing</h3>
          <div class="grid md:grid-cols-2 gap-4">
            <FieldLabel
              label="Marketing Campaign"
              locked={!canEditAwsField("marketingCampaign", reviewStatusForFieldLock)}
            >
              <input placeholder="Marketing Campaign" bind:value={form.marketingCampaign} class="input" disabled={!canEditAwsField("marketingCampaign", reviewStatusForFieldLock)} />
            </FieldLabel>
            <FieldLabel
              label="AWS Funding Used"
              locked={!canEditAwsField("awsFundingUsed", reviewStatusForFieldLock)}
            >
              <select
                value={form.awsFundingUsed || ""}
                disabled={!canEditAwsField("awsFundingUsed", reviewStatusForFieldLock)}
                on:change={(event) => (form.awsFundingUsed = event.currentTarget.value)}
                class="input"
              >
                <option value="">-- Select AWS Funding Used --</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
              {#if errors.awsFundingUsed}
                <p class="text-red-600 text-sm">{errors.awsFundingUsed}</p>
              {/if}
            </FieldLabel>
            <FieldLabel
              label="Marketing Source"
              locked={!canEditAwsField("marketingSource", reviewStatusForFieldLock)}
            >
              <select
                value={form.marketingSource || ""}
                disabled={!canEditAwsField("marketingSource", reviewStatusForFieldLock)}
                on:change={(event) => handleMarketingSourceChange(event.currentTarget.value)}
                class="input"
              >
                <option value="">-- Select Marketing Source --</option>
                {#each options?.marketingSource ?? [] as opt}
                  <option value={opt}>{opt}</option>
                {/each}
              </select>
              {#if errors.marketingSource}
                <p class="text-red-600 text-sm">{errors.marketingSource}</p>
              {/if}
            </FieldLabel>
          </div>
        </section>

        <!-- Arrays (AWS Products, Needs, Sales, Delivery, APN, Marketing Channels, Use Cases) -->
        <section>
          <h3 class="font-semibold mb-3 flex items-center gap-1.5">
            AWS Products
            <AwsLockIcon locked={!canEditAwsField("awsProducts", reviewStatusForFieldLock)} />
          </h3>
          <MultiCheckboxDropdown
            items={products}
            bind:selectedValues={form.awsProducts}
            placeholder="Select AWS products..."
            getItemValue={getProductValue}
            getItemLabel={getProductOptionLabel}
            getBadgeLabel={getProductBadgeLabel}
            disabled={!canEditAwsField("awsProducts", reviewStatusForFieldLock)}
          />
        </section>

        <section>
          <h3 class="font-semibold mb-3 flex items-center gap-1.5">
            Partner Primary Needs From AWS
            <AwsLockIcon locked={!canEditAwsField("partnerPrimaryNeedFromAws", reviewStatusForFieldLock)} />
          </h3>
          <button
            class="text-primary-600 hover:underline disabled:text-gray-400 disabled:no-underline"
            disabled={!canEditAwsField("partnerPrimaryNeedFromAws", reviewStatusForFieldLock)}
            on:click={() => addItem("partnerPrimaryNeedFromAws", "")}
          >
            + Add
          </button>
          {#each form.partnerPrimaryNeedFromAws as need, id}
            <div class="flex items-center gap-2 mt-2 relative">
              <select
                value={form.partnerPrimaryNeedFromAws[id] || ""}
                disabled={!canEditAwsField("partnerPrimaryNeedFromAws", reviewStatusForFieldLock)}
                on:change={(event) => (form.partnerPrimaryNeedFromAws[id] = event.currentTarget.value)}
                class="input"
              >
                <option value="">-- Select --</option>
                {#each options.primaryNeedsFromAws ?? [] as opt}
                  <option value={opt}>{opt}</option>
                {/each}
              </select>
              <button
                class="ace-remove-btn absolute top-2 right-3 p-1 rounded-full transition"
                aria-label="Remove partnerPrimaryNeedFromAws"
                disabled={!canEditAwsField("partnerPrimaryNeedFromAws", reviewStatusForFieldLock)}
                on:click={() => removeItem("partnerPrimaryNeedFromAws", id)}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          {/each}
          {#if errors.partnerPrimaryNeedFromAws}
            <p class="text-red-600 text-sm mt-2">{errors.partnerPrimaryNeedFromAws}</p>
          {/if}
        </section>

        <section>
          <h3 class="font-semibold mb-3 flex items-center gap-1.5">
            Delivery Models
            <AwsLockIcon locked={!canEditAwsField("deliveryModel", reviewStatusForFieldLock)} />
          </h3>
          <button
            class="text-primary-600 hover:underline disabled:text-gray-400 disabled:no-underline"
            disabled={!canEditAwsField("deliveryModel", reviewStatusForFieldLock)}
            on:click={() => addItem("deliveryModel", "")}>+ Add</button
          >
          {#each form.deliveryModel as d, id}
            <div class="flex items-center gap-2 mt-2 relative">
              <select
                value={form.deliveryModel[id] || ""}
                disabled={!canEditAwsField("deliveryModel", reviewStatusForFieldLock)}
                on:change={(event) => (form.deliveryModel[id] = event.currentTarget.value)}
                class="input"
              >
                <option value="">-- Select --</option>
                {#each options?.deliveryModel ?? [] as opt}
                  <option value={opt}>{opt}</option>
                {/each}
              </select>
              <button
                class="ace-remove-btn absolute top-2 right-3 p-1 rounded-full transition"
                aria-label="Remove deliveryModel"
                disabled={!canEditAwsField("deliveryModel", reviewStatusForFieldLock)}
                on:click={() => removeItem("deliveryModel", id)}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          {/each}
          {#if errors.deliveryModel}
            <p class="text-red-600 text-sm mt-2">{errors.deliveryModel}</p>
          {/if}
        </section>

        <section>
          <h3 class="font-semibold mb-3 flex items-center gap-1.5">
            Sales Activities
            <AwsLockIcon locked={!canEditAwsField("salesActivities", reviewStatusForFieldLock)} />
          </h3>
          <button
            class="text-primary-600 hover:underline disabled:text-gray-400 disabled:no-underline"
            disabled={!canEditAwsField("salesActivities", reviewStatusForFieldLock)}
            on:click={() => addItem("salesActivities", "")}>+ Add</button
          >
          {#each form.salesActivities as a, id}
            <div class="flex items-center gap-2 mt-2 relative">
              <select
                value={form.salesActivities[id] || ""}
                disabled={!canEditAwsField("salesActivities", reviewStatusForFieldLock)}
                on:change={(event) => (form.salesActivities[id] = event.currentTarget.value)}
                class="input"
              >
                <option value="">-- Select --</option>
                {#each options?.salesActivities ?? [] as opt}
                  <option value={opt}>{opt}</option>
                {/each}
              </select>
              <button
                class="ace-remove-btn absolute top-2 right-3 p-1 rounded-full transition"
                aria-label="Remove salesActivities"
                disabled={!canEditAwsField("salesActivities", reviewStatusForFieldLock)}
                on:click={() => removeItem("salesActivities", id)}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          {/each}
          {#if errors.salesActivities}
            <p class="text-red-600 text-sm mt-2">{errors.salesActivities}</p>
          {/if}
        </section>

        <!-- TODO: later we'll enable adding multiple APN programs -->
        <!-- <section>
          <h3 class="font-semibold mb-3">APN Programs</h3>
          <button
            class="text-primary-600 hover:underline"
            on:click={() => addItem("apnPrograms", "")}>+ Add</button
          >
          {#each form.apnPrograms as p, id}
            <div class="flex items-center gap-2 mt-2 relative">
              <select bind:value={form.apnPrograms[id]} class="input">
                <option value="">-- Select --</option>
                {#each options?.apnPrograms ?? [] as opt}
                  <option value={opt}>{opt}</option>
                {/each}
              </select>
              <button
                class="ace-remove-btn absolute top-2 right-3 p-1 rounded-full transition"
                aria-label="Remove apnPrograms"
                on:click={() => removeItem("apnPrograms", id)}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          {/each}
        </section> -->

        <section>
          <h3 class="font-semibold mb-3 flex items-center gap-1.5">
            APN Programs
            <AwsLockIcon locked={!canEditAwsField("apnPrograms", reviewStatusForFieldLock)} />
          </h3>
          <div class="flex items-center gap-2 mt-2 relative">
            <select
              value={form.apnPrograms[0] || ""}
              disabled={!canEditAwsField("apnPrograms", reviewStatusForFieldLock)}
              on:change={(event) => (form.apnPrograms[0] = event.currentTarget.value)}
              class="input"
            >
              <option value="">-- Select --</option>
              {#each options?.apnPrograms ?? [] as opt}
                <option value={opt}>{opt}</option>
              {/each}
            </select>
          </div>
        </section>

        <section>
          <h3 class="font-semibold mb-3 flex items-center gap-1.5">
            Marketing Activity Channel
            <AwsLockIcon locked={!canEditAwsField("marketingActivityChannel", reviewStatusForFieldLock)} />
          </h3>
          <button
            class="text-primary-600 hover:underline disabled:text-gray-400 disabled:no-underline"
            disabled={!canEditAwsField("marketingActivityChannel", reviewStatusForFieldLock)}
            on:click={() => addItem("marketingActivityChannel", "")}
          >
            + Add
          </button>
          {#each form.marketingActivityChannel as ch, id}
            <div class="flex items-center gap-2 mt-2 relative">
              <select
                value={form.marketingActivityChannel[id] || ""}
                disabled={!canEditAwsField("marketingActivityChannel", reviewStatusForFieldLock)}
                on:change={(event) =>
                  (form.marketingActivityChannel[id] = event.currentTarget.value)}
                class="input"
              >
                <option value="">-- Select --</option>
                {#each options?.marketingChannel ?? [] as opt}
                  <option value={opt}>{opt}</option>
                {/each}
              </select>
              <button
                class="ace-remove-btn absolute top-2 right-3 p-1 rounded-full transition"
                aria-label="Remove marketingActivityChannel"
                disabled={!canEditAwsField("marketingActivityChannel", reviewStatusForFieldLock)}
                on:click={() => removeItem("marketingActivityChannel", id)}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          {/each}

          {#if errors.marketingActivityChannel}
            <p class="text-red-600 text-sm">{errors.marketingActivityChannel}</p>
          {/if}
        </section>

        <section>
          <h3 class="font-semibold mb-3 flex items-center gap-1.5">
            Marketing Activity Use Cases
            <AwsLockIcon locked={!canEditAwsField("marketingActivityUseCase", reviewStatusForFieldLock)} />
          </h3>
          <button
            class="text-primary-600 hover:underline disabled:text-gray-400 disabled:no-underline"
            disabled={!canEditAwsField("marketingActivityUseCase", reviewStatusForFieldLock)}
            on:click={() => addItem("marketingActivityUseCase", "")}>+ Add</button
          >
          {#each form.marketingActivityUseCase as u, id}
            <div class="flex items-center gap-2 mt-2 relative">
              <select
                value={form.marketingActivityUseCase[id] || ""}
                disabled={!canEditAwsField("marketingActivityUseCase", reviewStatusForFieldLock)}
                on:change={(event) =>
                  (form.marketingActivityUseCase[id] = event.currentTarget.value)}
                class="input"
              >
                <option value="">-- Select --</option>
                {#each options?.marketingUseCase ?? [] as opt}
                  <option value={opt}>{opt}</option>
                {/each}
              </select>
              <button
                class="ace-remove-btn absolute top-2 right-3 p-1 rounded-full transition"
                aria-label="Remove marketingActivityUseCase"
                disabled={!canEditAwsField("marketingActivityUseCase", reviewStatusForFieldLock)}
                on:click={() => removeItem("marketingActivityUseCase", id)}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          {/each}

          {#if errors.marketingActivityUseCase}
            <p class="text-red-600 text-sm">{errors.marketingActivityUseCase}</p>
          {/if}
        </section>

        <!-- Security -->
        <section>
          <h3 class="font-semibold mb-3 flex items-center gap-1.5">
            Does Opportunity Belong to National Security?
            <AwsLockIcon locked={!canEditAwsField("doesOppBelongToNatSec", reviewStatusForFieldLock)} />
          </h3>
          <select
            value={form.doesOppBelongToNatSec || ""}
            disabled={!canEditAwsField("doesOppBelongToNatSec", reviewStatusForFieldLock)}
            on:change={(event) => (form.doesOppBelongToNatSec = event.currentTarget.value)}
            class="input"
          >
            <option value="">-- Select --</option>
            {#each options?.nationalSecurityFlag ?? [] as opt}
              <option value={opt}>{opt}</option>
            {/each}
          </select>
          {#if errors.doesOppBelongToNatSec}
            <p class="text-red-600 text-sm">{errors.doesOppBelongToNatSec}</p>
          {/if}
        </section>
      </div>

      <section class="mt-6 border-t border-white/10 pt-4">
        <button
          type="button"
          class="flex w-full items-center justify-between py-2 text-left"
          on:click={() => (showMandatoryFillPanel = !showMandatoryFillPanel)}
        >
          <span>
            <span class="block font-semibold text-gray-100">AWS mandatory field fill</span>
            <span class="block text-sm text-gray-400">
              Add optional opportunity context and fill missing AWS mandatory fields.
            </span>
          </span>
          <span class="text-xl leading-none text-gray-400">
            {showMandatoryFillPanel ? "-" : "+"}
          </span>
        </button>

        {#if showMandatoryFillPanel}
          <div class="mt-3 space-y-4">
            <textarea
              placeholder="Add any extra context for this opportunity..."
              bind:value={form.mandatoryFieldContext}
              class="input min-h-[88px]"
            />
            {#if fillSummaryMessages.length}
              <div class="p-4 bg-blue-500/10 border border-blue-500/30 text-blue-300 rounded text-sm">
                <p class="font-semibold mb-2">Fill result</p>
                <ul class="list-disc pl-5 space-y-1">
                  {#each fillSummaryMessages as msg}
                    <li>{msg}</li>
                  {/each}
                </ul>
              </div>
            {/if}

            <div class="flex items-center justify-between gap-4">
              <p class="text-sm text-gray-400">
                Use this before saving to apply suggested values.
              </p>
              <button
                class="popup-secondary-button px-4 py-2 rounded-lg disabled:opacity-50"
                on:click={handleFillMandatoryFields}
                disabled={isSaving || isFillingMandatoryFields}
                title="Fill missing AWS mandatory fields from opportunity context."
              >
                {isFillingMandatoryFields ? "Filling..." : "Fill AWS Mandatory Fields"}
              </button>
            </div>
          </div>
        {/if}
      </section>

      <!-- Error -->
      {#if validationSummaryMessages.length}
        <div
          bind:this={validationSummaryElement}
          class="mt-4 mb-4 p-3 bg-red-500/10 border border-red-500/40 text-red-300 rounded-lg text-sm"
        >
          <p class="font-semibold mb-2">Please fix the following before saving:</p>
          <ul class="list-disc pl-5 space-y-1">
            {#each validationSummaryMessages as msg}
              <li>{msg}</li>
            {/each}
          </ul>
        </div>
      {/if}

      <!-- Footer -->
      <div class="flex justify-end gap-4 mt-8">
        <button
          class="popup-secondary-button px-4 py-2 rounded-lg"
          on:click={() => {
            resetForm();
            showForm = false;
          }}
        >
          Cancel
        </button>
        <button
          class="px-4 py-2 bg-primary-600 text-white rounded-lg shadow hover:bg-primary-700 disabled:opacity-50"
          on:click={handleSubmit}
          disabled={isSaving || isFillingMandatoryFields}
        >
          {isSaving ? "Saving..." : "Save & Sync"}
        </button>
      </div>
    </div>
  </div>
{/if}
{#if showSuccessModal}
  <div class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center px-4 z-50">
    <div class="opportunity-popup-panel rounded-xl shadow-strong p-6 text-center" style={`width: min(640px, calc(100vw - 2rem)); ${themeCssVars}`}>
      <h2 class="text-xl font-bold text-green-400 mb-4">
        {successCloseConfirmedAfterError || successStageConfirmedAfterError
          ? `${successTerminalStage} Saved`
          : successPreservedAwsState
            ? `Opportunity Already ${successTerminalStage}`
            : "✅ Opportunity Saved"}
      </h2>
      <p class="text-white/80 mb-4">{successMessage}</p>
      {#if successHasWarning}
        <div class="mb-4 rounded-lg bg-amber-500/10 border border-amber-500/40 px-4 py-3 text-sm text-amber-200 text-center">
          <p>⚠️ <strong>Review Status</strong> was not updated. AWS controls this value for the current opportunity state.</p>
        </div>
      {/if}
      {#if successStageWarning}
        <div class="mb-4 rounded-lg bg-amber-500/10 border border-amber-500/50 px-4 py-3 text-sm text-amber-100 text-center">
          ⚠️ {successStageWarning}
        </div>
      {/if}
      <button
        class="px-4 py-2 bg-primary-600 text-white rounded-lg shadow hover:bg-primary-700"
        on:click={() => {
          showSuccessModal = false;
          resetForm();
          showForm = false;
          triggerOpportunityDataRefresh();
        }}
      >
        Close
      </button>
    </div>
  </div>
{/if}

<style>
  .ace-item-card {
    border: 1px solid var(--ace-border-subtle);
  }
  .ace-remove-btn {
    color: var(--ace-placeholder);
  }
  .ace-remove-btn:hover {
    color: #f87171;
    background: rgba(239, 68, 68, 0.15);
  }
  .input {
    background: var(--ace-surface);
    border: 1px solid var(--ace-border);
    border-radius: 0.5rem;
    color: var(--ace-text);
    padding: 0.5rem;
    width: 100%;
  }
  .input::placeholder {
    color: var(--ace-placeholder);
  }
  .input:disabled {
    background: var(--ace-surface-muted);
    color: var(--ace-disabled-text);
    cursor: not-allowed;
    opacity: 1;
  }
  .input {
    color-scheme: dark;
  }
  select.input option {
    background: var(--ace-surface);
    color: var(--ace-text);
  }
  .input:focus {
    outline: none;
    border-color: var(--ace-focus);
    box-shadow: 0 0 0 2px rgb(96 165 250 / 0.35);
  }
  .opportunity-popup-panel {
    background: var(--ace-panel);
    border: 1px solid var(--ace-border-panel);
    color: var(--ace-text);
  }
  .popup-secondary-button {
    background: var(--ace-button);
    border: 1px solid var(--ace-border-strong);
    color: var(--ace-text);
  }
  .popup-secondary-button:hover {
    background: var(--ace-button-hover);
  }
  .history-panel {
    background: var(--ace-surface);
    border-color: var(--ace-border-subtle);
  }
  .opportunity-popup-panel :global(section),
  .opportunity-popup-panel :global(h3),
  .opportunity-popup-panel :global(h4) {
    color: inherit;
  }
</style>
